/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * runScan
 * -------
 * One scan, start to finish. There is no Check in / Check out switch: the
 * pass's own status says what a scan means.
 *
 *   not arrived   → check in, straight away (asks first when late or after the end)
 *   checked in    → offer Check out, with the check-in time; nothing saved until
 *                   the organizer taps it (confirmCheckOut). Within RESCAN_MIN of
 *                   the check-in it is taken as the same pass scanned twice at
 *                   the entrance, and Check out is only offered as "anyway".
 *   checked out   → refused, with both times (a pass checks in and out once)
 *
 * A check-in is exactly three requests:
 *
 *   1. find the pass            /table/fetch            event_pass
 *   ·  is its event open?       clock only, no request
 *   2. registration confirmed?  /table/fetch/<id>/      event_registration — and which activity
 *   ·  scanned before?          the pass's own `status`, read in step 1
 *   ·  late or after the end?   → ask the organizer before saving
 *   3. save                     /table/save/records     check row + history line + pass status
 *
 * A check-out is those two reads, one more for the pass's times (step 2b,
 * event_pass_event_log), and the save when Check out is tapped.
 *
 * WHY THE PASS STATUS IS ENOUGH. The status is written in the same
 * all-or-nothing save as the check-in and check-out rows, so it always agrees
 * with them. It is the fast path, not the guard: the database allows one
 * check-in and one check-out per pass, so a save that races another scanner
 * fails, and then — only then — the row is read to say when it happened.
 *
 * There is one scanner for every event and activity: nothing here compares a
 * pass against a chosen event or activity — the pass says which event, its
 * registration says which activity.
 *
 * Outcomes:
 *   { kind: 'ok' }        saved; carries `scan` for updating counts and the list locally
 *   { kind: 'checkout' }  checked in earlier; `checkOut` is what confirmCheckOut saves
 *   { kind: 'rescan' }    checked in minutes ago; the same, offered as "anyway"
 *   { kind: 'stop' }      refused, with the reason; nothing saved
 *   { kind: 'confirm' }   valid but late / after the end; rerun with lateConfirmed
 *   { kind: 'choose' }    a typed code exists in several open events; rerun with eventId
 *   { kind: 'error' }     a request failed; nothing saved — scanning again is safe
 */

import {
  CHECK_IN_TABLE, CHECK_OUT_TABLE,
  findPasses, fetchRegistration, fetchCheckRow, fetchPassTimes, saveCheck,
} from './checkInApi';
import {
  eventWindow, idOf, clockIn, activityLabel, nameOf, LATE_AFTER_MIN, RESCAN_MIN,
} from './checkInRules';
import { parseStoredUtc } from '../../../utils/zonedDateTime';

const CONFIRMED = 'CONFIRMED';

function outcome(kind, title, detail, extra = {}) {
  return { kind, title, detail, ...extra };
}

/**
 * The "check in anyway?" question for a check-in after the late mark. With no
 * late limit configured it is asked for anyone after the start, so it says how
 * long ago the event started rather than naming a cut-off that does not exist.
 */
function lateQuestion(timing, event, extra) {
  if (LATE_AFTER_MIN == null) {
    const started = timing.minutesLate < 1 ? 'Event has just started' : `Event started ${agoText(timing.minutesLate)}`;
    return outcome('confirm', started, 'The pass is valid. Do you still want to check them in?', extra);
  }
  return outcome('confirm', `${durationText(timing.minutesLate)} late`,
    `The pass is valid, but they arrived after the ${clockIn(timing.lateAt, event.timezone)} cut-off. Check in anyway?`,
    extra);
}

const READ_FAILED = outcome(
  'error',
  'Couldn’t check this pass',
  'The connection or the server didn’t answer. Nothing was saved — scan again.'
);

/**
 * @param {object}   args
 * @param {object}   args.api             APIHelper (or a fake in tests)
 * @param {object}   args.input           { qrToken } or { passcode }
 * @param {Date}     args.now
 * @param {object}   args.eventsById      the organizer's events, by id
 * @param {object}   args.activitiesById  their activities, by id
 * @param {number[]} args.openEventIds    events open for check-in right now
 * @param {number}   args.userId          the signed-in organizer
 * @param {number}   [args.eventId]       the event picked when a code matched several
 * @param {boolean}  [args.lateConfirmed] the organizer already said "check in anyway"
 */
export default async function runScan({
  api, input, now, eventsById, activitiesById, openEventIds, userId,
  eventId: pickedEventId, lateConfirmed = false,
}) {
  // ── 1. Whose pass is this? ──────────────────────────────────────────────
  let passes;
  try {
    passes = await findPasses(api, {
      qrToken: input.qrToken,
      passcode: input.passcode,
      eventIds: pickedEventId != null ? [pickedEventId] : openEventIds,
    });
  } catch (error) {
    return READ_FAILED;
  }

  // RLS hides passes for other organizers' events, so they land here too.
  passes = passes.filter((row) => eventsById[idOf(row.event_id)]);
  if (passes.length === 0) {
    return outcome('stop', 'Pass not found', input.qrToken
      ? 'This QR isn’t a pass for any of your events.'
      : 'No pass with this code in your events open for check-in. Check the code and try again.');
  }
  if (passes.length > 1) {
    return outcome('choose', 'Which event is this pass for?',
      `Code ${input.passcode} is used in ${passes.length} of your open events. Ask the participant, then pick one.`,
      { eventIds: passes.map((row) => idOf(row.event_id)) });
  }

  const pass = passes[0];
  const passId = pass.id;
  const event = eventsById[idOf(pass.event_id)];
  const who = { name: nameOf(pass.user_info), passcode: pass.passcode, eventTitle: event.title, eventId: event.id };

  // What this scan means comes from the pass (step 1), not from a switch.
  const passStatus = String(pass.status || '').toUpperCase();
  const checkedIn = passStatus === 'CHECKED_IN';
  const checkedOut = passStatus === 'CHECKED_OUT';

  // ── Is this event open? (clock only) ────────────────────────────────────
  const timing = eventWindow(event, now);
  if (checkedIn && timing.checkOutClosed) {
    return outcome('stop', 'Check-out has closed for this event',
      `${event.title} stopped taking check-outs at ${clockIn(timing.checkOutClosesAt, event.timezone)}.`, who);
  }
  if (!checkedIn && !checkedOut && timing.phase === 'not_open') {
    return outcome('stop', 'Check-in hasn’t opened for this event',
      `${event.title} opens for check-in at ${clockIn(timing.opensAt, event.timezone)}.`, who);
  }

  // ── 2. Still registered, and for which activity? ────────────────────────
  let registration;
  try {
    registration = await fetchRegistration(api, idOf(pass.registration_id));
  } catch (error) {
    return READ_FAILED;
  }
  const status = String(registration?.status || '').toUpperCase();
  if (status !== CONFIRMED) {
    const word = status ? status.toLowerCase() : 'missing';
    return outcome('stop', `Registration is ${word}`,
      `This pass’s registration for ${event.title} is ${word}, so it can’t be used.`, who);
  }
  const shown = { ...who, activity: activityLabel(event, registration, activitiesById) };

  // ── 2b. Been here already? Say when, and offer the next step. ───────────
  if (checkedIn || checkedOut) {
    let times = null;
    try { times = (await fetchPassTimes(api, [passId]))[passId] || null; } catch (error) { times = null; }
    const inAt = times?.in ? clockIn(times.in, event.timezone) : null;
    if (checkedOut) {
      const outAt = times?.out ? clockIn(times.out, event.timezone) : null;
      return outcome('stop', 'Already checked out',
        inAt && outAt
          ? `Checked in ${inAt}, checked out ${outAt}. A pass checks in and out once.`
          : 'This pass has already checked in and out. A pass checks in and out once.',
        shown);
    }
    const checkOut = { ...shown, passId };
    const since = minutesSince(times?.in, now);
    if (since != null && since < RESCAN_MIN) {
      return outcome('rescan', `Just checked in, ${since < 1 ? 'moments' : `${since} min`} ago`,
        'Looks like the same pass scanned twice at the entrance. Nothing was changed.', { ...shown, checkOut });
    }
    return outcome('checkout', inAt ? `Checked in at ${inAt}` : 'Checked in',
      `${since != null ? `${agoText(since)}. ` : ''}Leaving now? Check them out.`, { ...shown, checkOut });
  }

  // ── Late, or after the end? Ask first — nobody is turned away. ──────────
  if (!lateConfirmed && (timing.phase === 'late' || timing.phase === 'ended')) {
    return timing.phase === 'ended'
      ? outcome('confirm', 'End time has passed',
        `${event.title} ended at ${clockIn(timing.end, event.timezone)}, ${durationText(timing.minutesAfterEnd)} ago. The pass is valid. Check in anyway?`,
        { ...shown, eventId: event.id })
      : lateQuestion(timing, event, { ...shown, eventId: event.id });
  }

  // ── 3. Save ─────────────────────────────────────────────────────────────
  return save(api, { mode: 'in', passId, event, shown, now, userId });
}

/**
 * The organizer tapped Check out on a `checkout` or `rescan` outcome: save it.
 * The reads were made by the scan a moment ago, so this is one request. The
 * database allows one check-out per pass, so a second scanner doing the same
 * cannot double it — save() then reports when it happened.
 */
export function confirmCheckOut({ api, result, eventsById, now, userId }) {
  const { passId, eventId, ...shown } = result.checkOut;
  return save(api, { mode: 'out', passId, event: eventsById[eventId], shown: { ...shown, eventId }, now, userId });
}

/** Whole minutes from a stored UTC time to `now`, or null when unknown. */
function minutesSince(storedUtc, now) {
  const at = parseStoredUtc(storedUtc);
  return at ? Math.max(0, Math.floor((now.getTime() - at.getTime()) / 60000)) : null;
}

/** "2 h 10 min", "45 min", "2 h". */
function durationText(minutes) {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${minutes} min`;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

/** "2 h 10 min ago", "45 min ago", "Just now". */
function agoText(minutes) {
  if (minutes < 1) return 'Just now';
  return `${durationText(minutes)} ago`;
}

/** Step 3: save the check-in or check-out and say what happened. */
async function save(api, { mode, passId, event, shown, now, userId }) {
  const checkingIn = mode === 'in';
  const table = checkingIn ? CHECK_IN_TABLE : CHECK_OUT_TABLE;
  const whenColumn = checkingIn ? 'checked_in_at' : 'checked_out_at';
  const done = () => outcome('ok', checkingIn ? 'Checked in' : 'Checked out',
    `${event.title} · ${clockIn(now, event.timezone)}`,
    {
      ...shown,
      scan: {
        passId, passcode: shown.passcode, name: shown.name, eventId: event.id,
        type: checkingIn ? 'CHECKED_IN' : 'CHECKED_OUT', at: now.toISOString(),
      },
    });
  try {
    await saveCheck(api, { mode, passId, eventId: event.id, userId, at: now });
    return done();
  } catch (saveError) {
    // Look before saying anything: another scanner may have saved this pass a
    // moment earlier (the database allows one row per pass), or our own save
    // may have landed and only the answer was lost.
    let landed;
    try {
      landed = await fetchCheckRow(api, table, passId);
    } catch (error) {
      return outcome('error', 'Not sure it saved',
        'The save didn’t answer and we couldn’t check. Scan the pass again — it won’t be counted twice.', shown);
    }
    if (landed) {
      const by = idOf(landed[checkingIn ? 'checked_in_by_user_id' : 'checked_out_by_user_id']);
      if (saveError.status === null && by === userId) return done();
      return outcome('stop', checkingIn ? 'Already checked in' : 'Already checked out',
        `${checkingIn ? 'Checked in' : 'Checked out'} at ${clockIn(landed[whenColumn], event.timezone)}.`, shown);
    }
    const said = saveError.body?.error || saveError.body?.detail || saveError.body?.message;
    const reason = typeof said === 'string' ? said : '';
    return outcome('error', checkingIn ? 'Couldn’t save this check-in' : 'Couldn’t save this check-out',
      `${reason ? `${reason} ` : ''}Nothing was saved — try again.`, shown);
  }
}
